/**
 * Authentication Context for BOSI application.
 *
 * Provides authentication state and methods (login, logout, register)
 * to all components via React Context API per research.md.
 */

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as authService from '../services/auth';

interface OnboardingStatus {
  completed: boolean;
  dismissed: boolean;
  current_page: number; // Feature 016: 0=not started, 1-4=page number
  pages_completed: string[]; // Feature 016: ['personal_details', 'goals', etc.]
  first_shown_at: string | null;
  last_updated_at: string | null; // Feature 016: last page save timestamp
  completed_at: string | null;
  // Feature 017: Body Insight Summary tracking
  body_insight_current_page?: number | null;
  body_insight_started_at?: string | null;
  body_insight_completed_at?: string | null;
}

interface User {
  id: string;
  email: string;
  profile: {
    first_name?: string | null;
    last_name?: string | null;
    date_of_birth?: string | null;
    telephone?: string | null;
    age_range?: string | null;
    life_stage?: string | null;
    primary_goal?: string | null;
    physical_profile?: {
      height?: { value: number; unit: string } | null;
      weight?: { value: number; unit: string } | null;
      target_weight?: { value: number; unit: string } | null;
      target_waist?: { value: number; unit: string } | null;
      preferred_unit_system?: string;
    };
    wellness_baseline?: {
      cycle_status?: string;
      activity_level?: string;
      sleep_quality_baseline?: string;
    };
    struggles?: string[];
    created_at: string;
  };
  settings?: {
    timezone: string;
    notification_preferences: {
      daily_reminder: boolean;
      reminder_time: string;
    };
  };
  onboarding: OnboardingStatus;
  created_at: string;
  updated_at?: string;
  last_login?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, password: string, profile?: any) => Promise<User>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  // Track active login/register to prevent checkAuth from overriding
  const isAuthenticatingRef = useRef(false);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      setIsLoading(true);
      const currentUser = await authService.getCurrentUser();
      // Only set user if no login/register is in progress
      if (!isAuthenticatingRef.current) {
        setUser(currentUser);
      }
    } catch (error) {
      if (!isAuthenticatingRef.current) {
        setUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    isAuthenticatingRef.current = true;
    // Clear all cached data from previous user
    queryClient.clear();

    try {
      const response = await authService.login(email, password);
      setUser(response.user);
      return response.user;
    } finally {
      isAuthenticatingRef.current = false;
    }
  };

  const register = async (email: string, password: string, profile?: any) => {
    isAuthenticatingRef.current = true;
    // Clear all cached data from previous user
    queryClient.clear();

    try {
      const response = await authService.register(email, password, profile);
      setUser(response.user);
      return response.user;
    } finally {
      isAuthenticatingRef.current = false;
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);

    // Clear all cached data on logout
    queryClient.clear();
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    checkAuth,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
